/**
 * Reports — the index.
 *
 * ## Why this is a hub of pages rather than a strip of tabs
 *
 * The set this replaces was five tabs on one screen, one per query, in the order the queries had
 * been easy to write. The user's verdict was *"the current is not what I would prefer at all"*,
 * and the reason a flat list of every report anybody ever asked for does not satisfy is that a
 * reader has to already know which of the five holds the thing they came for.
 *
 * So the reports are grouped by the **question** somebody is asking when they open them, and the
 * groups are the ones an agency actually asks on a Monday morning:
 *
 * - **The money** — am I collecting, who owes me, and where does what arrives end up
 * - **The space** — is anything empty, and what is let for how much
 * - **What is coming** — everything with a date on it in the next quarter
 * - **The development book** — what has been released, what has gone, and is the plan being paid
 *
 * ## And why each card carries a live figure
 *
 * An index whose cards say only what a report is *about* makes somebody open all six to find the
 * one that has changed. Every card here carries the report's own headline — the same number the
 * report prints when it opens, from the same server-side helper, because `/reports/overview`
 * calls the very functions the six endpoints call rather than working anything out again. A figure
 * on this page that disagreed with the page it opens would be worse than no figure at all.
 *
 * The sparklines beside those figures are `aria-hidden` and carry no table, deliberately: each one
 * sits beside a number already written out in words, so the text equivalent is the sentence it is
 * in. Every chart that carries information of its own goes through `ChartFrame` and gets a table —
 * see `components/charts/kit.tsx`.
 */

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { AgeingBucket } from "@rentorng/core";
import { api, downloadCsv } from "@/lib/api";
import { titleCase, useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { useActiveModule } from "@/lib/module-rail";
import { useModuleGate } from "@/lib/gating";
import { AGEING_SCALE, Meter, MiniStack, SERIES, Sparkline } from "@/components/charts";
import { Padlock } from "@/components/padlock";
import { Button, Card, EmptyState, ErrorState, PageHeader, Spinner } from "@/components/ui";
import {
  IconBuilding,
  IconChart,
  IconContract,
  IconEstate,
  IconInvoice,
  IconPayout,
} from "@/components/icons";

interface Overview {
  asOf: string;
  modules: { letting: boolean; development: boolean };
  collections: {
    totalBilled: number;
    totalCollected: number;
    rate: number;
    series: Array<{ key: string; billed: number; collected: number; gap: number }>;
  } | null;
  arrears: {
    grandTotal: number;
    overdue: number;
    debtors: number;
    totals: Record<AgeingBucket, number>;
    oldest: number;
  } | null;
  occupancy: {
    rate: number;
    vacant: number;
    lostRent: number;
    series: Array<{ key: string; units: number; occupied: number; rate: number }>;
  } | null;
  pipeline: {
    expiries: number;
    overdueRenewals: number;
    reviews: number;
    overdueReviews: number;
    reservations: number;
    instalments: number;
    instalmentValue: number;
    days: number;
    covers: { letting: boolean; development: boolean };
  };
  money: {
    from: string;
    to: string;
    collected: number;
    /** What the agency collected for itself. Never a share of anybody's rent — ADR-0131. */
    agencyIncome: number;
    recorded: number;
    recharged: number;
    toOwners: number;
    landlords: number;
  } | null;
  sales: {
    noun: { one: string; many: string };
    released: number;
    sold: number;
    available: number;
    scheduled: number;
    paid: number;
    overdue: number;
    schemes: number;
  } | null;
}

export function ReportsPage() {
  const fmt = useFormat();
  const { vocab, modules } = useSession();
  const developmentGate = useModuleGate("developing");
  /*
    **Reports is a screen both rails carry, and this index used to answer for the account rather
    than for the reader.** `data.modules.letting` says the organisation *holds* letting, so an
    account holding both was offered Collections, Arrears ageing, Occupancy, Rent roll and the
    expiry schedule while standing in the development rail — and every row inside those five links
    to a tenancy, a property or a reminder, which are letting-owned addresses. One press and the
    whole navigation had moved to the other business. That is the crossing the operator reported.

    So the index answers for the module in force. Nothing is hidden from anybody: the letting
    reports are one switch away, on the rail they belong to. The padlocked advert below is the one
    thing that is deliberately *not* narrowed — see the note on it.
  */
  const active = useActiveModule(modules);
  const showLetting = active === "letting";
  const showDevelopment = active === "developing";

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["reports-overview"],
    queryFn: () => api.get<Overview>("/reports/overview"),
  });

  return (
    <div>
      <PageHeader
        title="Reports"
        subtitle={
          data
            ? `As at ${fmt.date(data.asOf)}`
            : undefined
        }
        description="Grouped by the question they answer, not by the table they came from. Every figure here is the one the report itself prints, and everything exports."
        actions={
          <>
            <Button variant="secondary" onClick={() => downloadCsv("arrears")}>Arrears CSV</Button>
            <Button variant="secondary" onClick={() => downloadCsv("invoices")}>Invoices CSV</Button>
            <Button variant="secondary" onClick={() => downloadCsv("payments")}>Payments CSV</Button>
            <Button variant="secondary" onClick={() => downloadCsv("leases")}>{titleCase(vocab.leases)} CSV</Button>
          </>
        }
      />

      {isLoading ? <Spinner /> : null}
      {error ? <ErrorState error={error} retry={() => refetch()} /> : null}

      {data ? (
        <div className="space-y-8">
          {data.modules.letting && showLetting ? (
            <Group
              title="The money"
              question="What was billed, what arrived, who is behind, and where the difference went."
            >
              <ReportCard
                to="/app/reports/collections"
                icon={<IconChart size={18} />}
                title="Collections"
                question="Am I collecting what I billed?"
              >
                {data.collections ? (
                  <>
                    <Headline
                      value={fmt.money(data.collections.totalCollected, { compact: true })}
                      caption={`collected of ${fmt.money(data.collections.totalBilled, { compact: true })} billed, twelve months`}
                      chart={
                        <Sparkline
                          values={data.collections.series.map((point) => point.collected)}
                          kind="bars"
                          colour={SERIES[0]}
                        />
                      }
                    />
                    <div className="mt-3">
                      <Meter
                        value={data.collections.totalCollected}
                        max={data.collections.totalBilled}
                        label="Collection rate"
                        colour={data.collections.rate >= 85 ? SERIES[0] : data.collections.rate >= 60 ? "#d97706" : "#dc2626"}
                      />
                    </div>
                  </>
                ) : null}
              </ReportCard>

              <ReportCard
                to="/app/reports/arrears"
                icon={<IconInvoice size={18} />}
                title="Arrears ageing"
                question="Who owes me, and for how long?"
              >
                {data.arrears ? (
                  <>
                    <Headline
                      value={fmt.money(data.arrears.overdue, { compact: true })}
                      caption={`past due across ${data.arrears.debtors} ${data.arrears.debtors === 1 ? "debtor" : "debtors"}`}
                    />
                    <div className="mt-3">
                      {/* The bar is `aria-hidden`; the run of figures under it is the text
                          equivalent, and it is what a reader without the picture gets. */}
                      <MiniStack
                        segments={[
                          { label: "1–30 days", value: data.arrears.totals.d1_30, colour: AGEING_SCALE.d1_30 },
                          { label: "31–60 days", value: data.arrears.totals.d31_60, colour: AGEING_SCALE.d31_60, texture: "hatch" },
                          { label: "61–90 days", value: data.arrears.totals.d61_90, colour: AGEING_SCALE.d61_90 },
                          { label: "90+ days", value: data.arrears.totals.d90_plus, colour: AGEING_SCALE.d90_plus, texture: "grid" },
                        ]}
                      />
                      <p className="tabular mt-2 text-xs text-ink-500">
                        {(
                          [
                            ["1–30", data.arrears.totals.d1_30],
                            ["31–60", data.arrears.totals.d31_60],
                            ["61–90", data.arrears.totals.d61_90],
                            ["90+", data.arrears.totals.d90_plus],
                          ] as Array<[string, number]>
                        )
                          .filter(([, amount]) => amount > 0)
                          .map(([band, amount]) => `${band} ${fmt.money(amount, { compact: true })}`)
                          .join(" · ") || "Nothing past its due date"}
                      </p>
                    </div>
                  </>
                ) : null}
              </ReportCard>

              <ReportCard
                to="/app/reports/money"
                icon={<IconPayout size={18} />}
                title="Where the money goes"
                question="Of what came in, what did we keep and what is owed out?"
              >
                {/*
                  Led by what was collected for the owners rather than by what is owed to them.

                  `toOwners` is the obvious headline and it is the one figure on this payload that
                  cannot be trusted: the server subtracts the agency's own income from a total built
                  only from the owners' collections, so it is short by exactly the agency's fees.
                  The note at the top of `reports/MoneyFlow.tsx` has the measurement. Two figures
                  that are each right beat one that is nearly right, and the report itself is one
                  press away.
                */}
                {data.money ? (
                  <Headline
                    value={fmt.money(data.money.collected, { compact: true })}
                    caption={`collected for ${vocab.landlords} · ${fmt.money(data.money.agencyIncome, {
                      compact: true,
                    })} collected by this agency`}
                  />
                ) : null}
              </ReportCard>
            </Group>
          ) : null}

          {data.modules.letting && showLetting ? (
            <Group title="The space" question="What is let, what is empty, and what the empty ones are costing.">
              <ReportCard
                to="/app/reports/occupancy"
                icon={<IconBuilding size={18} />}
                title="Occupancy and voids"
                question="Is anything empty, and for how long?"
              >
                {data.occupancy ? (
                  <>
                    <Headline
                      value={`${data.occupancy.rate}%`}
                      caption={`let · ${data.occupancy.vacant} empty, ${fmt.money(data.occupancy.lostRent, { compact: true })} of rent lost`}
                      chart={<Sparkline values={data.occupancy.series.map((point) => point.rate)} colour={SERIES[1]} />}
                    />
                  </>
                ) : null}
              </ReportCard>

              <ReportCard
                to="/app/reports/rent-roll"
                icon={<IconContract size={18} />}
                title="Rent roll"
                question={`Every live ${vocab.lease}, what it pays and what it owes.`}
              />
            </Group>
          ) : null}

          <Group title="What is coming" question="Everything with a date on it, inside the next quarter.">
            <ReportCard
              to="/app/reports/pipeline"
              icon={<IconChart size={18} />}
              title={`The next ${data.pipeline.days} days`}
              question="What is about to expire, lapse or fall due?"
            >
              <Headline
                value={String(
                  data.pipeline.expiries + data.pipeline.reviews + data.pipeline.reservations + data.pipeline.instalments,
                )}
                caption={[
                  data.pipeline.covers.letting ? `${data.pipeline.expiries} expiring` : null,
                  /* Only when there are some. An agency that has recorded no review date would
                     otherwise read "0 to review" on the index for ever, which teaches them that the
                     figure means nothing rather than that they have none. */
                  data.pipeline.covers.letting && data.pipeline.reviews ? `${data.pipeline.reviews} to review` : null,
                  data.pipeline.covers.development && data.pipeline.reservations
                    ? `${data.pipeline.reservations} reservations lapsing`
                    : null,
                  data.pipeline.covers.development ? `${data.pipeline.instalments} instalments due` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              />
              {data.pipeline.overdueRenewals > 0 ? (
                <p className="mt-3 text-xs font-medium text-danger-700">
                  {data.pipeline.overdueRenewals} {data.pipeline.overdueRenewals === 1 ? "term has" : "terms have"} already
                  run out and been neither renewed nor ended.
                </p>
              ) : null}
            </ReportCard>

            {data.modules.letting && showLetting ? (
              <ReportCard
                to="/app/reports/expiries"
                icon={<IconContract size={18} />}
                title="Expiry schedule"
                question={`Every ${vocab.lease} with an end date, soonest first.`}
              />
            ) : null}
          </Group>

          {/* The locked door is drawn rather than hidden — ADR-0114. An agency without the
              development module is shown what it would get, with the padlock that says so, in the
              same place the report would sit if they had it. */}
          {(data.modules.development && showDevelopment) || developmentGate ? (
            <Group title="The development book" question="What has been released, what has gone, and is the plan being paid.">
              {data.modules.development && showDevelopment ? (
                <ReportCard
                  to="/app/reports/sales"
                  icon={<IconEstate size={18} />}
                  title="Sales and releases"
                  question="How is the book selling, and is the money arriving on plan?"
                >
                  {data.sales ? (
                    <>
                      <Headline
                        value={`${data.sales.sold} of ${data.sales.released}`}
                        caption={`${data.sales.noun.many} sold across ${data.sales.schemes} ${
                          data.sales.schemes === 1 ? "scheme" : "schemes"
                        }`}
                      />
                      <div className="mt-3">
                        <Meter
                          value={data.sales.paid}
                          max={data.sales.scheduled}
                          label="Paid against the plan"
                          caption={
                            data.sales.overdue > 0
                              ? `${fmt.money(data.sales.overdue, { compact: true })} past its due date`
                              : "Nothing past its due date"
                          }
                          colour={SERIES[0]}
                        />
                      </div>
                    </>
                  ) : null}
                </ReportCard>
              ) : (
                <Card>
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-surface)] bg-ink-100 text-ink-500">
                      <IconEstate size={18} />
                    </span>
                    <div className="min-w-0">
                      <h3 className="flex items-center gap-2 text-sm font-semibold text-ink-800">
                        Sales and releases
                        <Padlock gate={developmentGate} label="Development" />
                      </h3>
                      <p className="mt-1 text-sm text-ink-500">
                        Units sold against released, by phase and by type, and the payment plan against what has
                        actually arrived.
                      </p>
                    </div>
                  </div>
                </Card>
              )}
            </Group>
          ) : null}

          {!(data.modules.letting && showLetting) && !(data.modules.development && showDevelopment) ? (
            <Card>
              <EmptyState title="There is nothing to report on yet." hint="Add a property or a scheme and the reports fill themselves in." />
            </Card>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** A group of reports under the question they all answer some part of. */
function Group({ title, question, children }: { title: string; question: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="mb-3">
        <h2 className="text-base font-semibold text-ink-900">{title}</h2>
        <p className="mt-0.5 text-sm text-ink-500">{question}</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">{children}</div>
    </section>
  );
}

/**
 * One report on the index.
 *
 * The whole card is the link, because a card with a small "Open" at the bottom is a target
 * somebody has to aim at — the same reasoning as `StatTile`, which is a link for the same reason.
 */
function ReportCard({
  to,
  icon,
  title,
  question,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  question: string;
  children?: React.ReactNode;
}) {
  return (
    <Link to={to} className="card group flex flex-col p-5 transition-colors hover:border-ink-300 hover:bg-ink-50">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-surface)] bg-brand-100 text-brand-700">
          {icon}
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-ink-900">{title}</h3>
          <p className="mt-0.5 text-sm leading-snug text-ink-500">{question}</p>
        </div>
      </div>
      {children ? <div className="mt-4">{children}</div> : null}
    </Link>
  );
}

/** The report's own headline: the figure, what it means, and — sometimes — its shape. */
function Headline({ value, caption, chart }: { value: string; caption: string; chart?: React.ReactNode }) {
  return (
    <div className="flex items-end justify-between gap-3">
      <div className="min-w-0">
        <div className="tabular text-[22px] font-semibold leading-none tracking-tight text-ink-900">{value}</div>
        <p className="mt-1.5 text-xs leading-snug text-ink-500">{caption}</p>
      </div>
      {chart ? <div className="shrink-0">{chart}</div> : null}
    </div>
  );
}
